import React from 'react'
import styled from 'styled-components'
import { Box, Link, Space, TableColumn, TextBody } from '@ally/metronome-ui'
import { Row } from 'react-table'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import {
  Account,
  AccountLOB,
  AccountStatus,
} from '@ally/data-types-afg-accounts'
import dayjs from 'dayjs'
import { trackCCAccount } from '../analytics'
import {
  formatBalance,
  getDashDash,
  isRestrictedCCStatus,
  getCreditCardName,
} from '../utils'
import { AccountDetailsLink } from '../Shared/AccountDetailsLink'
import { DashboardView } from '../CustomizeDashboard/types'
import { CreditCardNames } from '../types'
import WarningIcon from '../Shared/WarningIcon'
import { STALE_DATA_COPY } from '../constants'

function getAllyTM({ type }: Account, ctaType?: string): string {
  if (ctaType) {
    return `${type}-${ctaType}`
  }
  return `creditCard-creditCardAccount-${type}-Card`
}

const StyledFooterBox = styled(Box)`
  display: flex;
  align-items: center;
  white-space: nowrap;

  > * {
    /* without this the icon shrinks to 0px wide */
    flex-shrink: 0;
  }
`

const StyledAccountNumberBox = styled(Box)`
  display: inline-block;
  vertical-align: middle;
`

interface GetColumnsProps {
  tableTypeHeader: CreditCardNames
  view: DashboardView
  isLgDown: boolean
  allCCAccounts: Account[]
}

export const getDescription = (account: Account): string | null => {
  switch (account.meta?.statusDescription) {
    case 'PAST_DUE':
    case 'OVERDUE':
      return `
      Looks like you missed a payment for your ${getCreditCardName(
        account,
      )} ${maskAccountNumber(account.accountNumber)}.`
    case 'OVER_LIMIT':
      return "You're over your credit limit."
    default:
      return null
  }
}

export const getStatus = (account: Account): string | null => {
  switch (account.status) {
    case 'BANKRUPT':
      return "You can't access this account."
    case 'LOST':
    case 'STOLEN':
    case 'FROZEN_OR_FRAUD':
      return "You can't access this account right now."
    case 'CLOSED':
      return 'Account closed'
    default:
      return null
  }
}

export const getWarningMessage = (account: Account): string | null => {
  return getStatus(account) || getDescription(account)
}

export const getColumns = ({
  tableTypeHeader,
  view,
  isLgDown,
  allCCAccounts,
}: GetColumnsProps): TableColumn<Account>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown

  const columns = {
    accountName: {
      id: 'name',
      accessor: (account: Account): string => {
        return account.nickName || account.name
      },
      Header: tableTypeHeader,
      alignment: 'left',
      isSortable: true,
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactNode | string => {
        return (
          <>
            <AccountDetailsLink
              text={account.nickName || account.name}
              account={account}
              key={account.id}
              allytm={getAllyTM(account)}
            />
            <StyledAccountNumberBox aria-hidden="true">
              {maskAccountNumber(account.accountNumber)}
            </StyledAccountNumberBox>
          </>
        )
      },
      Footer: ({ rows }: { rows: Row<Account>[] }): JSX.Element => {
        const account = rows[0].original

        return (
          <StyledFooterBox>
            <WarningIcon />
            <TextBody tag="span" size="sm" weight="bold">
              {getWarningMessage(account)}
            </TextBody>
          </StyledFooterBox>
        )
      },
    },
    currentBalance: {
      id: 'currentBalance',
      accessor: 'balance.current',
      isSortable: true,
      Header: 'Current Balance',
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactNode => {
        const hasCurrentBalance = typeof account.balance?.current === 'number'
        const currentBalance = account.balance?.current ?? 0
        const isClosedWithValue =
          account.status === AccountStatus.CLOSED && hasCurrentBalance
        if (isRestrictedCCStatus(account.status) && !isClosedWithValue) {
          const assistiveText = getStatus(account) || '--'
          return getDashDash(assistiveText)
        }
        if (!hasCurrentBalance) {
          return getDashDash(STALE_DATA_COPY)
        }
        if (account.lob === AccountLOB.CC) {
          return asCurrency(currentBalance)
        }
        return formatBalance(currentBalance)
      },
    },
    availableCredit: {
      id: 'availableCredit',
      accessor: 'meta.availableCredit',
      isSortable: true,
      Header: 'Available Credit',
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactNode => {
        const hasAvailableCredit =
          typeof account.meta?.availableCredit === 'number'
        const availableCredit = account.meta?.availableCredit ?? 0
        const isClosedWithValue =
          account.status === AccountStatus.CLOSED && hasAvailableCredit
        if (isRestrictedCCStatus(account.status) && !isClosedWithValue) {
          const assistiveText = getStatus(account) || '--'
          return getDashDash(assistiveText)
        }
        if (!hasAvailableCredit) {
          return getDashDash(STALE_DATA_COPY)
        }
        return formatBalance(availableCredit)
      },
    },
    paymentDueDate: {
      id: 'paymentDueDate',
      accessor: 'meta.paymentDueDate',
      Header: isDefaultView ? 'Payment Due Date' : 'Due Date',
      alignment: 'right',
      isSortable: true,
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactNode | string => {
        const isClosedWithValue =
          account.status === AccountStatus.CLOSED &&
          account.meta?.paymentDueDate
        if (isRestrictedCCStatus(account.status) && !isClosedWithValue) {
          const assistiveText = getStatus(account) || '--'
          return getDashDash(assistiveText)
        }
        if (!account.meta?.paymentDueDate) {
          return getDashDash(STALE_DATA_COPY)
        }
        const date = new Date(account.meta?.paymentDueDate)
        // API is sending us a UTC time of 00:00 and when localized it can unintentionally alter the day/month
        const [timeStrippedDueDate] = date.toISOString().split('T')
        const paymentDueDate = dayjs(timeStrippedDueDate).format('MMM DD, YYYY')
        return <span>{paymentDueDate}</span>
      },
    },
    actions: {
      id: 'actions',
      Header: 'Actions',
      alignment: 'right',
      Cell: ({
        row: { original: account },
      }: {
        row: Row<Account>
      }): React.ReactElement<Element> => {
        const assistiveText = getStatus(account) || '--'
        const restrictedStatuses = [
          'BANKRUPT',
          'FROZEN_OR_FRAUD',
          'STOLEN',
          'LOST',
        ]
        const allRestrictedCCAccounts = allCCAccounts
          .map(cc => cc.status)
          .every(status => restrictedStatuses.includes(status))
        const hasCurrentBalance = typeof account.balance?.current === 'number'
        const hasBalanceAboveZero = account.balance?.current
          ? account.balance.current > 0
          : false

        if (restrictedStatuses.includes(account.status)) {
          return (
            <Link
              to="/contact"
              text="Contact Us"
              allytmln={getAllyTM(account, 'contactUs')}
              onClick={(): void => trackCCAccount(account)}
            />
          )
        }

        if (allRestrictedCCAccounts) {
          return getDashDash(assistiveText)
        }

        if (isRestrictedCCStatus(account.status)) {
          if (account.status === AccountStatus.CLOSED && hasBalanceAboveZero) {
            return (
              <Link
                to={`/sso/credit-card/ePay/MakePayment?accountProxyId=${account.id}`}
                text="Pay Card"
                allytmln={getAllyTM(account, 'payCard')}
                onClick={(): void => trackCCAccount(account)}
              />
            )
          }
          return getDashDash(assistiveText)
        }

        if (account.meta?.statusDescription === 'NOT_ACTIVATED') {
          return (
            <Link
              to={`/sso/credit-card/Activate/Activate?accountProxyId=${account.id}`}
              text="Activate Card"
              allytmln={getAllyTM(account, 'activateCard')}
              onClick={(): void => trackCCAccount(account)}
            />
          )
        }

        if (!hasCurrentBalance) {
          return getDashDash(STALE_DATA_COPY)
        }

        const getTargetedMessage = (): string | null => {
          switch (account.meta?.statusDescription) {
            case 'PAST_DUE':
            case 'OVERDUE':
              return 'Past Due'
            case 'OVER_LIMIT':
              return 'Over Limit'
            default:
              return null
          }
        }

        const targetedMessage = getTargetedMessage()

        const linkText = `Pay Card${
          targetedMessage ? ` (${targetedMessage})` : ''
        }`

        const payCardLink = (
          <Link
            to={`/sso/credit-card/ePay/MakePayment?accountProxyId=${account.id}`}
            text={linkText}
            allytmln={getAllyTM(account, 'payCard')}
            onClick={(): void => trackCCAccount(account)}
          />
        )

        if (account.meta?.paymentDue) {
          return (
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="flex-end"
              alignItems="center"
            >
              {!targetedMessage ? (
                <TextBody tag="span" size="sm">
                  {formatBalance(account.meta.paymentDue)} min due
                </TextBody>
              ) : null}
              <Space ml="xxs">{payCardLink}</Space>
            </Box>
          )
        }

        return payCardLink
      },
    },
  }

  const tileViewCols = [
    columns.accountName,
    columns.currentBalance,
    columns.availableCredit,
    columns.actions,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountName,
    columns.currentBalance,
    columns.availableCredit,
    columns.paymentDueDate,
    columns.actions,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
